import { UnreachableCaseError } from "libs/errors";
import { useRecordLoader, UseRecordLoaderResult } from "./useRecordLoader";
import { ClientRecordLoaderObserveQueryResult } from "~/environment/RecordLoader";
import { distinctUntilChanged, map, Observable } from "rxjs";
import { omit } from "lodash-comms";
import { isEqual } from "libs/predicates";
import { useInitialQueryLimit } from "./useInitialQueryLimit";
import { observeFilteredThreadEntriesForVirtualList } from "~/components/content-list/ThreadEntry";
import { TTagViewType } from "./useTagViewType";

export type UseTagViewThreadsResult = UseRecordLoaderResult<string[]>;

export function useTagViewThreads(props: {
  tagId: string | null | undefined;
  type: TTagViewType | null;
}): UseTagViewThreadsResult {
  const initialLimit = useInitialQueryLimit(100);

  return useRecordLoader({
    name: "useTagViewThreads",
    load({ loader, limit, currentUserId, deps: [tagId, type] }) {
      let observable: Observable<ClientRecordLoaderObserveQueryResult<"thread">>;

      if (!tagId || !type) {
        observable = loader.createObserveQueryResult<"thread">();
      } else {
        switch (type) {
          case "label": {
            observable = loader.observeGetTagViewThreads({
              currentUserId,
              tagId,
              sortDirection: "DESC",
              limit,
            });

            break;
          }
          case "group": {
            observable = loader.observeGetGroupViewThreads({
              currentUserId,
              groupId: tagId,
              sortDirection: "DESC",
              limit,
            });

            break;
          }
          default: {
            throw new UnreachableCaseError(type);
          }
        }
      }

      return observeFilteredThreadEntriesForVirtualList({ recordLoader: loader }, observable).pipe(
        map(([threads, meta]) => {
          const threadIds = threads.map((thread) => thread.id);
          return [threadIds, omit(meta, "recordMap")] as const;
        }),
        distinctUntilChanged(isEqual),
      );
    },
    initialLimit,
    limitStep: initialLimit,
    deps: [props.tagId, props.type],
  });
}
